import { BasicButton, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useCaseMutation from "@/hooks/useCaseMutation";
import { LETTER_TYPE, LETTER_TYPE_VALUE } from "@/libraries/constant";
import { today } from "@/libraries/date";
import { isAxiosError } from "@/libraries/helper";
import type { NewCaseLetterFormFieldSchema } from "@/schemas";
import { usePrompt } from "@/stores/prompt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import omit from "lodash/omit";
import { useCallback, useEffect } from "react";
import {
	Controller,
	type SubmitHandler,
	useFormContext,
} from "react-hook-form";
import { toast } from "sonner";

dayjs.extend(advancedFormat);

export const Route = createFileRoute(
	"/dashboard/case/view/$id/letter/new/info",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const onBack = useBackward();
	const navigate = Route.useNavigate();
	const {
		watch,
		control,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitting },
	} = useFormContext<NewCaseLetterFormFieldSchema>();

	const caseId = watch("caseId");
	const letterType = watch("case_letter.letter_type");

	const openPrompt = usePrompt((state) => state.open);

	const { createLetter } = useCaseMutation();

	const onSubmit = useCallback<SubmitHandler<NewCaseLetterFormFieldSchema>>(
		(data, e) => {
			e?.stopPropagation();
			e?.preventDefault();
			return createLetter.mutateAsync(data, {
				onSuccess: (res) => {
					openPrompt({
						title: "Send Later",
						description:
							"Would you like to send this letter as email now? You can send the letter immediately or send it later.",
						positiveButton: {
							label: "Send as Email",
							onClick: () =>
								navigate({
									to: "/dashboard/case/view/$id/letter/$letterId/export",
									params: {
										id: data.caseId,
										letterId: res.case_letter.id,
									},
								}),
						},
						negativeButton: {
							label: "Send Later",
							onClick: () => {
								toast.success(
									`${LETTER_TYPE_VALUE[data.case_letter.letter_type]} added`,
								);
								onBack("/dashboard/case/view/$id/detail", {
									replace: true,
									params: {
										// @ts-expect-error
										id: data.caseId,
									},
									search: {
										// @ts-expect-error
										tab: "letters",
									},
								});
							},
						},
					});
				},
				onError: (err) => {
					if (isAxiosError(err)) {
						return toast.error((err.response?.data as any)?.messages);
					}
					toast.error("Failed, please try again");
				},
			});
		},
		[openPrompt, createLetter, navigate, onBack],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!letterType && caseId)
			navigate({
				to: "/dashboard/case/view/$id/letter/new",
				params: {
					id: caseId,
				},
				replace: true,
			});
	}, [letterType, caseId]);

	return (
		<>
			<BackAppBar
				title={`Add ${LETTER_TYPE_VALUE[letterType ?? LETTER_TYPE.DISCHARGE_LETTER]}`}
			/>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="flex-1 flex flex-col overflow-hidden"
			>
				<div className="flex-1 bg-gray-light flex flex-col gap-2 overflow-y-auto">
					<div className="container bg-white flex flex-col gap-6 py-4">
						<Typography fontWeight={900} fontSize={14}>
							Date
						</Typography>
						<Controller
							control={control}
							name="case_letter.date"
							render={({ field }) => {
								return (
									<div className="w-full">
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<DatePicker
												label="Select Date"
												className="!w-full [&_.MuiInputBase-root]:!rounded-xl"
												format="DD MMMM YYYY"
												disableFuture={true}
												maxDate={today()}
												slots={{
													openPickerIcon: CalendarTodayIcon,
												}}
												{...omit(field, ["ref"])}
											/>
										</LocalizationProvider>
										<Typography
											fontWeight={400}
											fontSize={12}
											className="pl-4 text-[#00000099]"
										>
											Select the relevant date for this letter
										</Typography>
									</div>
								);
							}}
						/>
						<Controller
							control={control}
							name="case_letter.time"
							render={({ field }) => {
								return (
									<div className="w-full">
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<TimePicker
												label="Select Time"
												format="hh:mm A"
												ampm
												className="!w-full [&_.MuiInputBase-root]:!rounded-xl"
												{...omit(field, ["ref"])}
											/>
										</LocalizationProvider>
										<Typography
											fontWeight={400}
											fontSize={12}
											className="pl-4 text-[#00000099]"
										>
											Select the relevant date for this letter
										</Typography>
									</div>
								);
							}}
						/>
					</div>
					<div className="container bg-white flex flex-col gap-6 py-4">
						<Typography fontWeight={900} fontSize={14}>
							Letter Details
						</Typography>
						<Controller
							control={control}
							name="case_letter.diagnosis"
							render={({ field, fieldState }) => (
								<TextField
									label="Diagnosis"
									placeholder="Enter Diagnosis"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Primary diagnosis or condition treated during admission"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
						<Controller
							control={control}
							name="case_letter.medication"
							render={({ field, fieldState }) => (
								<TextField
									multiline
									rows={3}
									label="Medication"
									placeholder="Enter Medication"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Current medications with dosage and duration"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
						<Controller
							control={control}
							name="case_letter.allergies"
							render={({ field, fieldState }) => (
								<TextField
									multiline
									rows={3}
									label="Allergies"
									placeholder="Enter Allergies"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Document any known drug allergies or adverse reactions"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
						<Controller
							control={control}
							name="case_letter.summary"
							render={({ field, fieldState }) => (
								<TextField
									multiline
									rows={3}
									label="Case Summary / Message"
									placeholder="Enter Case Summary"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Main content and clinical information"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
						<Controller
							control={control}
							name="case_letter.plan"
							render={({ field, fieldState }) => (
								<TextField
									multiline
									rows={3}
									label="Plan"
									placeholder="Enter Plan"
									className="[&_.MuiInputBase-root]:!rounded-xl"
									helperText="Recommendations and next steps"
									error={!!fieldState.error?.message}
									{...omit(field, ["ref"])}
								/>
							)}
						/>
					</div>
				</div>
				<div className="container bg-white py-4 shadow">
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						className="!h-12 text-sm font-bold !shadow-none"
						fullWidth
						disable={!isDirty || !isValid}
						loading={isSubmitting}
					>
						Submit
					</BasicButton>
				</div>
			</form>
		</>
	);
}
